import React, { useRef } from 'react';
import { useFrame } from '@react-three/fiber';
import { Html } from '@react-three/drei';
import * as THREE from 'three';
import { equipmentLabelStyle } from './styles';

interface RubberStopperProps {
  visible: boolean;
  /** World position. Backwards-compatible default keeps Naphthalene behaviour. */
  position?: [number, number, number];
  rotation?: [number, number, number];
  scale?: number;
  /** When > 0, render a visible hole through the centre of the stopper
   *  so a glass tube can be shown passing through it. */
  holeRadius?: number;
  /** Hide the "Rubber stopper" floating label. */
  hideLabel?: boolean;
}

const RubberStopper: React.FC<RubberStopperProps> = ({
  visible,
  position = [-2.0, -0.34, 0],
  rotation = [0, 0, 0],
  scale = 1,
  holeRadius = 0,
  hideLabel = false,
}) => {
  const groupRef = useRef<THREE.Group>(null);
  const scaleRef = useRef(0);

  useFrame(() => {
    if (!groupRef.current) return;
    scaleRef.current += ((visible ? scale : 0) - scaleRef.current) * 0.08;
    groupRef.current.scale.setScalar(scaleRef.current);
  });

  return (
    <group ref={groupRef} position={position} rotation={rotation} scale={0}>
      {/* Solid stopper body */}
      <mesh>
        <cylinderGeometry args={[0.11, 0.13, 0.18, 16]} />
        <meshStandardMaterial color="#2a1a0a" roughness={0.9} />
      </mesh>
      {/* Optional hole — visualised as a small dark cylinder embedded in the centre */}
      {holeRadius > 0 && (
        <mesh>
          <cylinderGeometry args={[holeRadius, holeRadius, 0.20, 18]} />
          <meshStandardMaterial color="#050505" roughness={1} side={THREE.DoubleSide} />
        </mesh>
      )}
      {visible && !hideLabel && (
        <Html position={[0, 0, 0]} center style={{ pointerEvents: 'none' }}>
          <div style={equipmentLabelStyle}>Rubber stopper</div>
        </Html>
      )}
    </group>
  );
};

export default RubberStopper;
